import './sp-audio-input-entry/sp-audio-input-entry';
import {customElement, property, query, state} from 'lit/decorators.js';
import {html, PropertyValues} from 'lit';
import {when} from 'lit/directives/when.js';
import {audioInput} from './sp-audio-input.styles';
import WaveSurfer from 'wavesurfer.js';
import {styleMap} from 'lit-html/directives/style-map.js';
import {repeat} from 'lit-html/directives/repeat.js';
import {v4 as uuidv4} from 'uuid';
import {classMap} from 'lit/directives/class-map.js';
import {translate} from '@stefanholzapfel/lit-translate';
import {translateUppercase} from '@stefanholzapfel/lit-translate/dist/translateUppercase.directive';
import {SpDefaultDialog} from '../../sp-dialog/sp-default-dialog';
import {SpFile, SpFileSource, SpRemoteFile} from '../interfaces';
import {SpBaseInput} from "../sp-base-input";
import {SpImageService, SpType} from "../../../services/sp-image.service";
import {SpValidationService} from "../../../services/sp-validation.service";
import RecordPlugin from "wavesurfer.js/dist/plugins/record.js";
import {unsafeCSSIcon} from "../../../styles/icons";

@customElement('sp-audio-input')
export class SpAudioInput extends SpBaseInput<(SpFile | SpRemoteFile)[]> {
    @property({type: Boolean, reflect: true})
    disabled = false;
    @property({type: Boolean})
    allowRecording = true;
    @property({type: Boolean})
    allowFilepicker = true;
    // A list of allowed mime types
    @property({type: Array})
    allowedFileTypes?: string[] = ['audio/*'];
    // Max. number of files allowed
    @property({type: Number})
    maxlength = 5;
    // Max. length of recordings in seconds
    @property({type: Number})
    maxRecordLength = 60;
    // Max. size of uploaded files in bytes
    @property({type: Number})
    maxsize = 6291456;
    @property({type: Array})
    value = [];
    @property({type: String})
    placeholder: string;
    @property({type: Boolean})
    viewMode = false;

    @state()
    recorderState: RecordingState = 'inactive';
    @state()
    errorMessages = [];

    recorderWaveSurfer: WaveSurfer;
    recorder: RecordPlugin;
    recorderInterval;
    checkingPermission = false;
    devicePresent = false;
    deviceReady = false;

    @query('#errorDialog') dialog: SpDefaultDialog;

    static styles = [
        audioInput
    ];

    protected willUpdate(_changedProperties: PropertyValues) {
        super.willUpdate(_changedProperties);
        if (!this.value) this.value = [];
    }

    protected async firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        // Check browser support
        if (!this.viewMode && this.allowRecording && navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
            try {
                // Check device presence and initialize WaveSurfer
                const devices = await navigator.mediaDevices.enumerateDevices();
                for (const device of devices) {
                    if (device.kind === 'audioinput') {
                        this.devicePresent = true;
                        this.initializeWaveSurfer();
                        this.checkPermission(true);
                        break;
                    }
                }
            } catch (e) {
                console.warn('No suitable device present on your browser!');
            }
        } else {
            if (!this.viewMode) console.warn('getUserMedia not supported on your browser!');
        }
    }

    initializeWaveSurfer() {
        this.recorderWaveSurfer = WaveSurfer.create({
            container: this.shadowRoot.querySelector('.wave') as HTMLElement,
            waveColor: getComputedStyle(this.shadowRoot.host).getPropertyValue('--sp-audio-input-wave-progress-color'),
            height: 45,
            interact: false,
            cursorWidth: 0,
            barWidth: 2,
            barRadius: 1.5,
            barGap: 1.5,
            hideScrollbar: true
        });
        this.recorder = this.recorderWaveSurfer.registerPlugin(RecordPlugin.create());
        this.recorder.on('record-end', async (blob) => {
            this.recorderState = 'inactive';
            const file = { blob, spec: {
                    id: uuidv4(),
                    source: 'recording' as SpFileSource,
                    type: 'gallery' as SpType
                } };
            this.value.push(file);
            this.emitChangeEvent(this.value);
            this.requestUpdate();
        });
        this.recorder.on('record-start', () => {
            this.recorderState = 'recording';
            const time = Date.now();
            this.recorderInterval = setInterval(() => {
                const seconds = (Date.now() - time) / 1000;
                this.shadowRoot.querySelector('.time').innerHTML = `${Math.trunc(seconds / 60).toString().padStart(2, '0')}:${Math.trunc(seconds % 60).toString().padStart(2, '0')}`;
                if (seconds > this.maxRecordLength) {
                    this.recorder.stopRecording();
                    clearInterval(this.recorderInterval);
                    this.errorMessages = [translate('UI_COMPONENT_AUDIO_INPUT_LENGTH_ERROR', { maxlength: this.maxRecordLength.toString()})];
                    this.dialog.show();
                }
            }, 100);
        });
    }

    fileAdd() {
        if (this.allowFilepicker && this.canAdd()) this.shadowRoot.getElementById('filePicker').click();
    }

    fileChange(changeEvent: Event) {
        const target = changeEvent.target as HTMLInputElement;
        if (!this.disabled && (!this.maxlength || this.value.length <= this.maxlength) && target.files?.length > 0) {
            const checkedFiles = this.checkFiles(Array.from(target.files));
            const files = checkedFiles.map(file => { return { blob: file, spec: {
                id: uuidv4(),
                source: 'localFile' as SpFileSource
            } } });
            this.value.push(...files);
            this.emitChangeEvent(this.value);
            target.value = '';
            this.requestUpdate();
        }
    }

    async toggleRecord(e: Event) {
        if (this.allowRecording && this.devicePresent) {
            if (this.deviceReady) {
                if (e.type === 'long-press-end') {
                    if (this.recorder.isRecording()) {
                        clearInterval(this.recorderInterval);
                        this.shadowRoot.querySelector('.time').innerHTML = '00:00';
                        this.recorder.stopRecording();
                    } else {
                        console.error("Recorder stopped before it started!");
                    }
                }
                if (e.type === 'long-press-start') {
                    this.recorder.startRecording();
                }
            } else if (e.type === 'long-press-start') {
                await this.checkPermission();
                if (this.deviceReady)
                    this.toggleRecord(e);
            }
        }
    }

    deleteEntity(index) {
        this.value.splice(index, 1);
        this.emitChangeEvent(this.value);
        this.requestUpdate();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.recorder?.isRecording()) this.recorder.stopRecording();
        this.recorderWaveSurfer?.destroy();
    }

    checkFiles(files: File[]): File[] {
        this.errorMessages = [];
        const validFiles = files.filter(file => {
            // Check filesize
            if (file.size > this.maxsize) {
                this.errorMessages.push(translate('UI_COMPONENT_AUDIO_INPUT_SIZE_ERROR', { filename: file.name, maxsize: this.maxsize.toString() }));
                return false;
            }
            // Check mime type
            if (!SpValidationService.validateMimeTypes(file.type, this.allowedFileTypes)) {
                this.errorMessages.push(translate('UI_COMPONENT_AUDIO_INPUT_MIMETYPE_ERROR', { filename: file.name, filetype: file.type }));
                return false;
            }
            return true;
        });
        if (this.errorMessages.length > 0) {
            this.dialog.show();
        }
        return validFiles;
    }

    protected emitChangeEvent(value: (SpFile|SpRemoteFile)[]): void {
        this.dispatchEvent(
            new SpAudioInputChangeEvent(
                {
                    bubbles: true, composed: true, detail: {
                        value
                    }
                })
        );
    }

    protected render() {
        return html`
            ${ when(!this.viewMode, () => 
                html`
                    <sp-default-dialog id="errorDialog"
                            .headerText=${ translateUppercase('UI_COMPONENT_ERROR') }
                            .template=${ this.errorMessages }
                            .buttons=${[{
                                clickCallback: () => { this.dialog.hide(true) },
                                textPosition: 'center',
                                text: translate('UI_COMPONENT_OK')
                            }]}></sp-default-dialog>
                    <div class="container"
                         style=${ styleMap({
                            cursor: this.canAdd() && this.allowFilepicker ? 'pointer' : 'default'
                         }) }
                         @click=${ () => { this.fileAdd() }}>
                        <div class="icon"
                             style=${styleMap({display: this.recorderState === 'recording' ? 'grid' : 'none'})}></div>
                        <div class="time"
                             style=${styleMap({display: this.recorderState === 'recording' ? 'grid' : 'none'})}></div>
                        <div class="wave"
                             style=${styleMap({display: this.recorderState === 'recording' ? 'grid' : 'none'})}></div>
                        <div class="text"
                             style=${styleMap({display: this.recorderState === 'recording' ? 'none' : 'grid'})}>${ this.placeholder }
                        </div>
                        <sp-button class="button" .icon="url(${unsafeCSSIcon('microphone')})"
                                   .useLongPress=${true}
                                   .iconAnimation=${ this.recorderState === 'recording' ? 'pulsating-circle' : null }
                                   ?disabled=${ !this.canAdd() }
                                   @long-press-start=${this.toggleRecord}
                                   @long-press-end=${this.toggleRecord}></sp-button>
                        ${ when(this.allowFilepicker, () => html`<input hidden id="filePicker" type="file" accept=${ this.allowedFileTypes.join(',') } @change=${this.fileChange}>`) }
                    </div>`)
            }
            ${ when(this.value.length > 0, () =>
                    html`<div class=${classMap({
                            recordings: true,
                            'view-mode': this.viewMode
                        })}>
                            ${repeat(this.value, value => value.spec.id, (value, index) => {
                                return html`
                                    <sp-audio-input-entry
                                            .value=${ value.hasOwnProperty('blob') ? (value as SpFile).blob : SpImageService.buildUrl((value as SpRemoteFile).spec.type, 'images', (value as SpRemoteFile).path) }
                                            .viewMode=${this.viewMode}
                                            @delete="${() => {
                                                this.deleteEntity(index)
                                            }}">
                                    </sp-audio-input-entry>`;
                            })}
                        </div>`) }`;
    }

    private canAdd() {
        return !this.disabled && (!this.maxlength || this.value.length < this.maxlength);
    }

    private async checkPermission(suppressError = false) {
        if (this.checkingPermission) {
            this.errorMessages = [translate('UI_COMPONENT_AUDIO_INPUT_AWAITING_PERMISSION')];
            this.dialog.show();
            return;
        }
        this.checkingPermission = true;
        try {
            // Try to access audio devices (this will request permission in browser if not permitted or denied already
            const stream = await navigator.mediaDevices.getUserMedia({audio: true});
            // Stop tracks to avoid record indicator in browser
            stream.getTracks().forEach((track) => {
                track.stop();
            });
            this.deviceReady = true;
        } catch (e) {
            console.warn("No access to audio device!");
            if (!suppressError) {
                this.errorMessages = [translate('UI_COMPONENT_AUDIO_INPUT_NOT_ALLOWED')];
                this.dialog.show();
            }
        } finally {
            this.checkingPermission = false;
        }
    }
}

export interface SpAudioInputChangeEventPayload {
    value: (SpFile|SpRemoteFile)[]
}

export class SpAudioInputChangeEvent extends CustomEvent<SpAudioInputChangeEventPayload> {
    constructor(options: CustomEventInit<SpAudioInputChangeEventPayload>) {
        super('change', options);
    }
}
